// crmLeadsVisitorLine.js — the one line a built app's sign-up form shows while
// its CRM sending is on (POB "CRM leads, opt-in per app"; Nika 2026-10-31
// 00:06, STATUS r124 option A):
//
//   en "Your details go to our team so we can contact you."
//   key workspace.apps.crmLeads.visitorLine (packages/shared/i18n/<lang>/workspace.json)
//
// WHO DECIDES. The server: sdk.ai.listExtensions answers `crmLeads: { enabled }`
// on each built-app row (AgentWorkspaceConversationController). A missing
// field reads OFF. The shell never infers it.
//
// WHICH FORM. The one the server reads as a sign-up form
// (server core/utils/appCrmLeadForms.js): an element whose `fetch` holds a
// records `insert` that carries `crmLead` (true, or a map with at least one of
// name / firstName / lastName / email / phone). The line goes INTO that form
// element as its last child, so it sits under the form's fields and its send
// button. An element that is not the form (no `tag: 'form'`, no
// `on: 'submit'`; a click-insert on a button) gets no line: a text inside a
// button would read as the button's label.
//
// WHERE IT RUNS. ONE function for every mount path:
//   - in-shell, after _rewriteFetchDecls (functions/index.js
//     buildInlineExtensionDef / buildInlineMultiPageDef);
//   - in the sandbox frame (functions/sandboxFrameBoot.js), which receives this
//     function as TEXT (sandboxBootstrapSource). So it is SELF-CONTAINED: no
//     closure over this module, no imports, ES2017 syntax only (no object
//     spread, no optional chaining), like sandboxFrameBoot itself.
// The text resolves through `el.call('polyglot', …)`: the shell's polyglot
// in-shell, the frame's pushed-bundle lookup in the sandbox.
//
// Walks element DEFINITIONS (never DOM), skips `state` / `fetch` / `scope` /
// `data` and function values (a `children` factory's rows are not walked: a
// sign-up form is a page part, not a list row). Idempotent: a definition that
// already has the line is left alone. Mutates `definition`; returns how many
// lines it added.
export function crmLeadsVisitorLine(definition, crmLeads) {
  'use strict'
  if (!crmLeads || typeof crmLeads !== 'object' || crmLeads.enabled !== true) return 0
  if (!definition || typeof definition !== 'object') return 0
  var LINE_KEY = 'CrmLeadsVisitorLine'
  var FIELD_KEYS = ['name', 'firstName', 'lastName', 'email', 'phone']
  var SKIP = { state: 1, fetch: 1, scope: 1, data: 1, attr: 1, style: 1 }
  var seen = []
  var added = 0

  var marksLead = function (mark) {
    if (mark === true) return true
    if (!mark || typeof mark !== 'object') return false
    for (var i = 0; i < FIELD_KEYS.length; i++) {
      var v = mark[FIELD_KEYS[i]]
      if (typeof v === 'string' && v.trim()) return true
    }
    return false
  }
  var onSubmit = function (on) {
    if (on === 'submit') return true
    return Array.isArray(on) && on.indexOf('submit') !== -1
  }
  // a records insert with a crmLead mark; `from` is bare in the frame and
  // namespaced (workspaceProject.records) after the in-shell rewrite
  var leadDecl = function (d) {
    if (!d || typeof d !== 'object' || !marksLead(d.crmLead)) return null
    if (d.from !== 'records' && d.from !== 'workspaceProject.records') return null
    if (d.method !== 'insert') return null
    return d
  }
  var isSignupForm = function (def) {
    var list = Array.isArray(def.fetch) ? def.fetch : [def.fetch]
    for (var i = 0; i < list.length; i++) {
      var d = leadDecl(list[i])
      if (d && (def.tag === 'form' || onSubmit(d.on))) return true
    }
    return false
  }
  var visit = function (def) {
    if (!def || typeof def !== 'object' || seen.indexOf(def) !== -1) return
    seen.push(def)
    if (Array.isArray(def)) {
      for (var i = 0; i < def.length; i++) visit(def[i])
      return
    }
    for (var k in def) {
      if (SKIP[k] === 1 || k === LINE_KEY) continue
      var v = def[k]
      if (v && typeof v === 'object') visit(v)
    }
    if (def.fetch && typeof def.fetch === 'object' && !def[LINE_KEY] && isSignupForm(def)) {
      def[LINE_KEY] = {
        extends: 'AppFieldHint',
        tag: 'p',
        margin: '0',
        text: function (el) {
          return el.call('polyglot', 'workspace.apps.crmLeads.visitorLine')
        },
        hide: false
      }
      added++
    }
  }
  visit(definition)
  return added
}
